import React, { type ReactNode, createContext, useContext, useEffect, useRef } from "react";

import { GetReviewCycleDetailsResponseFrontend } from "@performyard/service-contracts/src/review-cycle/response";
import { ReviewDetailsFrontend } from "@performyard/service-contracts/src/reviews/review-details";

import { useReviewCycleDetails, useReviewCycleReviewsDetailsStream } from "../../../../api/hooks/defs/review-cycle";

import { useReviewCycleStatus } from "../../../../api/websocket/review-cycle-status";

import ReviewCycleLoader from "../LoadingStates/ReviewCycleLoader";

type ReviewCycleBuilderPageContextType = {
	renderPage: ReactNode | null;
	fullRefresh: () => void;
	reviewCycleDetails?: GetReviewCycleDetailsResponseFrontend;
	reviewCycleReviewsDetails?: ReviewDetailsFrontend[];
};

const ReviewCycleBuilderPageContext = createContext<ReviewCycleBuilderPageContextType | undefined>(undefined);

type ReviewCycleBuilderPageProviderProps = {
	children: ReactNode;
	reviewCycleId: string;
};

export const ReviewCycleBuilderPageProvider = ({ children, reviewCycleId }: ReviewCycleBuilderPageProviderProps) => {
	// Use the new unified review cycle status WebSocket
	const {
		isConnected,
		isInitialized: statusInitialized,
		isConnecting,
		isBuilding,
		isAddingSubjects,
		isApprovingEasyAdds,
		isAnyJobRunning,
		isUpdating,
		resetConnection,
		connect,
		disconnect,
	} = useReviewCycleStatus(reviewCycleId, false);

	// Track when queries are enabled/disabled
	// Use either WebSocket status or fallback after timeout
	const webSocketReady = statusInitialized;

	const {
		data: reviewCycleDetails,
		isLoading: isReviewCycleDetailsLoading,
		refetch: refetchReviewCycleDetails,
	} = useReviewCycleDetails(reviewCycleId, {
		retry: (attemptIndex) => {
			// If we detect building via websockets, keep retrying quietly
			if (isAnyJobRunning) {
				return attemptIndex < 10; // Retry more when building is detected
			}
			// Normal retry logic - be more lenient initially in case of race conditions
			return attemptIndex < 5;
		},
		retryDelay: (attemptIndex) => {
			// Faster retries initially to handle race conditions with quick jobs
			if (attemptIndex <= 3) {
				return Math.min(100 * 2 ** attemptIndex, 500); // 100ms, 200ms, 400ms, 500ms
			}
			return Math.min(1000 * 2 ** (attemptIndex - 3), 5000); // Then longer delays
		},
		meta: {
			disableDefaultErrorHandling: true, // Always suppress errors - we handle them with our own loading states
		},
	});
	const {
		data: reviewCycleReviewsDetails,
		refetch: refetchReviewCycleReviewsDetails,
		resetQuery: resetReviewCycleReviewsDetailsQuery,
	} = useReviewCycleReviewsDetailsStream(reviewCycleId, undefined, {
		retry: (attemptIndex) => {
			// If we detect building via websockets, keep retrying quietly
			if (isAnyJobRunning) {
				return attemptIndex < 10; // Retry more when building is detected
			}
			// Normal retry logic - be more lenient initially in case of race conditions
			return attemptIndex < 5;
		},
		retryDelay: (attemptIndex) => {
			// Faster retries initially to handle race conditions with quick jobs
			if (attemptIndex <= 3) {
				return Math.min(100 * 2 ** attemptIndex, 500); // 100ms, 200ms, 400ms, 500ms
			}
			return Math.min(1000 * 2 ** (attemptIndex - 3), 5000); // Then longer delays
		},
		meta: {
			disableDefaultErrorHandling: true, // Always suppress errors - we handle them with our own loading states
		},
		staleTime: 0, // Override the infinite stale time to allow refetching when needed
		refetchOnMount: true, // Always refetch when component mounts
	});

	const fullRefresh = (): void => {
		// Reset WebSocket connection to get fresh status
		resetConnection();
		// Reset queries
		resetReviewCycleReviewsDetailsQuery();
		refetchReviewCycleDetails();
		refetchReviewCycleReviewsDetails();
	};

	// Refetch queries whenever job running state changes, but only after WS is connected/initialized
	const prevIsAnyJobRunningRef = useRef<boolean | null>(null);
	useEffect(() => {
		if (!statusInitialized || !isConnected) {
			return;
		}

		if (prevIsAnyJobRunningRef.current === null) {
			prevIsAnyJobRunningRef.current = isAnyJobRunning;
			return;
		}

		if (prevIsAnyJobRunningRef.current !== isAnyJobRunning) {
			// Job state toggled; refresh queries and let UI show loader when jobs run
			resetReviewCycleReviewsDetailsQuery();
			refetchReviewCycleDetails();
			refetchReviewCycleReviewsDetails();
			prevIsAnyJobRunningRef.current = isAnyJobRunning;
		}
	}, [
		isAnyJobRunning,
		statusInitialized,
		isConnected,
		resetReviewCycleReviewsDetailsQuery,
		refetchReviewCycleDetails,
		refetchReviewCycleReviewsDetails,
	]);

	const jobStatus = reviewCycleDetails?.jobStatus || "idle";

	// Scenario 1: If initial load shows preloader AND server reports a non-idle job,
	// initiate websocket connection and wait for handshake (statusInitialized) before proceeding
	useEffect(() => {
		if (!reviewCycleId) {
			return;
		}
		if (jobStatus !== "idle" && !statusInitialized && !isConnected && !isConnecting) {
			connect(reviewCycleId);
		}
	}, [reviewCycleId, jobStatus, statusInitialized, isConnected, isConnecting, connect]);

	// Scenario 2: Once content is visible, ensure websocket is connected non-blocking
	useEffect(() => {
		if (!reviewCycleId) {
			return;
		}
		const contentVisible =
			!isReviewCycleDetailsLoading && (statusInitialized || jobStatus === "idle") && !isAnyJobRunning;
		if (contentVisible && !isConnected && !isConnecting) {
			connect(reviewCycleId);
		}
	}, [
		reviewCycleId,
		isReviewCycleDetailsLoading,
		statusInitialized,
		jobStatus,
		isAnyJobRunning,
		isConnected,
		isConnecting,
		connect,
	]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (isConnected) {
				disconnect();
			}
		};
	}, [isConnected, disconnect]);

	// Render page logic based on job statuses
	let renderPage: ReactNode | null = null;
	if (isReviewCycleDetailsLoading || (!webSocketReady && jobStatus !== "idle") || isAnyJobRunning) {
		let text: string = "";
		if (isBuilding || reviewCycleDetails?.jobStatus === "building") {
			text = "Your Review Cycle is Still Building...";
		} else if (isAddingSubjects) {
			text = "Adding Subjects to the Cycle...";
		} else if (isApprovingEasyAdds) {
			text = "Approving 360 forms...";
		} else if (isUpdating || reviewCycleDetails?.jobStatus === "updating") {
			text = "Updating the Cycle...";
		} else {
			text = "Loading Review Cycle...";
		}
		renderPage = <ReviewCycleLoader text={text} />;
	} else {
		renderPage = null;
	}

	const value: ReviewCycleBuilderPageContextType = {
		renderPage,
		fullRefresh,
		reviewCycleDetails,
		reviewCycleReviewsDetails,
	};

	return <ReviewCycleBuilderPageContext.Provider value={value}>{children}</ReviewCycleBuilderPageContext.Provider>;
};

export const useReviewCycleBuilderPageContext = (): ReviewCycleBuilderPageContextType => {
	const context = useContext(ReviewCycleBuilderPageContext);
	if (context === undefined) {
		throw new Error("useReviewCycleBuilderPageContext must be used within a ReviewCycleBuilderPageProvider");
	}
	return context;
};
